import { captureException } from "@sentry/nextjs"
import { HttpsAgent } from "agentkeepalive"
import axios, { AxiosError, AxiosRequestConfig } from "axios"
import axiosRetry from "axios-retry"

import { customAdapter } from "services/adapter"
import {
  getAppleAuthToken,
  getAuthToken,
  getDeviceId,
  getGoogleAuthToken,
  getReCaptchaToken,
  getRefTaToken,
  getSoftLoginAuthToken,
  getTemporaryAuthToken,
  removeAppleAuthToken,
  removeAuthToken,
  removeGoogleAuthToken,
  removeReCaptchaToken,
} from "utils/local-storage"

import { apiBaseUrl, defaultTimeout } from "./clientConstants"

export const defaultHttpsAgent = new HttpsAgent({ keepAlive: true })

const defaultConfig: AxiosRequestConfig = {
  timeout: defaultTimeout,
  httpsAgent: defaultHttpsAgent,
  adapter: customAdapter,
  baseURL: apiBaseUrl,
  headers: {
    app: "SIGNATURE_STAYS",
    authorization: "",
  },
}

export const defaultAxiosInstance = axios.create(defaultConfig)
axiosRetry(defaultAxiosInstance)

defaultAxiosInstance.interceptors.request.use(
  (request) => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    if (request?.data?.avoidClientSideAuth) {
      // For server side rendring because we can not have client token there.
      return {
        ...request,
        headers: {
          ...request.headers,
          app: "SIGNATURE_STAYS",
        },
      }
    }

    const deviceID = getDeviceId()
    const refTaToken = getRefTaToken()

    const googleAuthToken = getGoogleAuthToken()
    const reCaptchaToken = getReCaptchaToken()
    removeReCaptchaToken()

    if (googleAuthToken && reCaptchaToken) {
      removeGoogleAuthToken()
      return {
        ...request,
        headers: {
          ...request.headers,
          "X-Google-Authorization": googleAuthToken,
          "whimstay-auth": `whimstay ${reCaptchaToken}`,
          app: "SIGNATURE_STAYS",
          ...(deviceID ? { "Device-ID": deviceID } : {}),
          ...(refTaToken ? { "X-User-Ref-TA": refTaToken } : {}),
        },
      }
    }

    const appleAuthToken = getAppleAuthToken()

    if (appleAuthToken && reCaptchaToken) {
      removeAppleAuthToken()
      return {
        ...request,
        headers: {
          ...request.headers,
          "X-Apple-Authorization": appleAuthToken,
          "whimstay-auth": `whimstay ${reCaptchaToken}`,
          app: "SIGNATURE_STAYS",
          ...(deviceID ? { "Device-ID": deviceID } : {}),
          ...(refTaToken ? { "X-User-Ref-TA": refTaToken } : {}),
        },
      }
    }

    const authToken = getAuthToken()
    const temporaryAuthToken = getTemporaryAuthToken()
    const softLoginAuthToken = getSoftLoginAuthToken()

    if (reCaptchaToken) {
      // removeTemporaryAuthToken()
      removeAuthToken()
      return {
        ...request,
        headers: {
          ...request.headers,
          authorization: temporaryAuthToken,
          "whimstay-auth": `whimstay ${reCaptchaToken}`,
          app: "SIGNATURE_STAYS",
          ...(deviceID ? { "Device-ID": deviceID } : {}),
          ...(refTaToken ? { "X-User-Ref-TA": refTaToken } : {}),
        },
      }
    }

    if (temporaryAuthToken) {
      removeAuthToken()
      return {
        ...request,
        headers: {
          ...request.headers,
          authorization: temporaryAuthToken,
          app: "SIGNATURE_STAYS",
          ...(deviceID ? { "Device-ID": deviceID } : {}),
          ...(refTaToken ? { "X-User-Ref-TA": refTaToken } : {}),
        },
      }
    }

    if (softLoginAuthToken) {
      removeAuthToken()
      return {
        ...request,
        headers: {
          ...request.headers,
          authorization: softLoginAuthToken,
          app: "SIGNATURE_STAYS",
          ...(deviceID ? { "Device-ID": deviceID } : {}),
          ...(refTaToken ? { "X-User-Ref-TA": refTaToken } : {}),
        },
      }
    }

    if (authToken) {
      return {
        ...request,
        headers: {
          ...request.headers,
          authorization: authToken,
          app: "SIGNATURE_STAYS",
          ...(deviceID ? { "Device-ID": deviceID } : {}),
          ...(refTaToken ? { "X-User-Ref-TA": refTaToken } : {}),
        },
      }
    }
    return {
      ...request,
      headers: {
        ...request.headers,
        app: "SIGNATURE_STAYS",
        ...(deviceID ? { "Device-ID": deviceID } : {}),
        ...(refTaToken ? { "X-User-Ref-TA": refTaToken } : {}),
      },
    }
  },
  async (error: AxiosError) => {
    // eslint-disable-next-line promise/no-promise-in-callback
    await Promise.reject(
      new Error(
        error
          ? error.message || error.name || JSON.stringify(error)
          : "Unknown error",
      ),
    )
  },
)

defaultAxiosInstance.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    const statusCode = error?.response?.status
    if (statusCode && statusCode >= 500) {
      captureException(
        new Error(
          `whimstayAPI Error: ${error?.message || "NA"} ${
            error?.response?.config?.url || "NA"
          }`,
        ),
        {
          tags: {
            ws_location: "RequestInterceptor",
          },
          extra: {
            readableMessage: `${error?.response?.status} ${error?.response?.statusText} ${error.message}`,
            apiPayload: JSON.stringify(error?.response?.request || {}),
            apiResponse: JSON.stringify(error?.response?.data || {}),
          },
        },
      )
    }
    return Promise.reject(error)
  },
)
